import { Box } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "../../../context/AuthContext";
import {
  useGetRoles,
  useCreateRole,
  useEditRole,
  useDeleteRole,
} from "../../../hooks/useRole";
import { useGetPermissions } from "../../../hooks/usePermission";
import { useSites } from "../../../hooks/useSite";
import type Role from "../../../types/role/Role";
import type Site from "../../../types/site/Site";
import type Permissions from "../../../types/permission/permission";
import ResourceInUseError from "../../../errors/ResourceInUseError";
import {
  toolbarBtnSx,
  flyoutInputSx,
  flyoutLabelSx,
  crudTableSx,
  selectedRowSx,
  radioInputSx,
  primaryCellSx,
  CrudPageLayout,
  LoadingState,
  EmptyState,
  FlyoutDrawer,
  FlyoutFooter,
  DeleteConfirmation,
  PlusIcon,
  flyoutLabels,
  formErrorSx,
} from "../../components/shared";

// ---------------------------------------------------------------------------
// Permission helpers
// ---------------------------------------------------------------------------

const FILTERED_PREFIXES = ["customization:"];
const FILTERED_PERMISSIONS = ["customers:*", "customers:Create", "customers:Delete"];

function shouldIncludePermission(perm: string): boolean {
  if (FILTERED_PREFIXES.some((p) => perm.startsWith(p))) return false;
  if (FILTERED_PERMISSIONS.includes(perm)) return false;
  return true;
}

function permissionLabel(perm: string): string {
  const parts = perm.split(":");
  return parts.length > 1 ? parts[1] : perm;
}

interface PermissionGroup {
  key: string;
  label: string;
  permissions: string[];
}

function buildPermissionGroups(perms: Permissions | undefined): PermissionGroup[] {
  if (!perms) return [];
  const groups: PermissionGroup[] = [
    { key: "auditLog", label: "Audit Logs", permissions: perms.auditLogPermissions },
    { key: "interaction", label: "Interactions", permissions: perms.interactionPermissions },
    { key: "role", label: "Roles", permissions: perms.rolePermissions },
    { key: "site", label: "Sites", permissions: perms.sitePermissions },
    { key: "user", label: "Users", permissions: perms.userPermissions },
    { key: "connector", label: "Connectors", permissions: perms.connectorPermissions },
    { key: "customers", label: "Customers", permissions: perms.customersPermissions },
    { key: "view", label: "Views", permissions: perms.viewPermissions },
    { key: "summaryTemplate", label: "Summary Templates", permissions: perms.summaryTemplatePermissions },
  ];
  return groups
    .map((g) => ({
      ...g,
      permissions: g.permissions.filter(shouldIncludePermission),
    }))
    .filter((g) => g.permissions.length > 0);
}

// ---------------------------------------------------------------------------
// Permissions Checkboxes Component
// ---------------------------------------------------------------------------

function PermissionsEditor({
  selectedPermissions,
  onChange,
}: Readonly<{
  selectedPermissions: Set<string>;
  onChange: (perms: Set<string>) => void;
}>) {
  const { t } = useTranslation(["roles"]);
  const { data: allPermissions } = useGetPermissions();
  const groups = useMemo(
    () => {
      const built = buildPermissionGroups(allPermissions);
      // Apply translated labels to permission groups
      return built.map((g) => ({
        ...g,
        label: t(`roles:${g.key}` as Parameters<typeof t>[0], { defaultValue: g.label }),
      }));
    },
    [allPermissions, t],
  );

  const togglePermission = (perm: string) => {
    const next = new Set(selectedPermissions);
    if (next.has(perm)) {
      next.delete(perm);
    } else {
      next.add(perm);
    }
    onChange(next);
  };

  const toggleGroup = (group: PermissionGroup) => {
    const next = new Set(selectedPermissions);
    const allSelected = group.permissions.every((p) => next.has(p));
    if (allSelected) {
      group.permissions.forEach((p) => next.delete(p));
    } else {
      group.permissions.forEach((p) => next.add(p));
    }
    onChange(next);
  };

  if (groups.length === 0) {
    return (
      <Box sx={{ color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>
        {t("roles:loadingPermissions")}
      </Box>
    );
  }

  const renderGroup = (group: PermissionGroup) => {
    const allSelected = group.permissions.every((p) => selectedPermissions.has(p));
    const someSelected = !allSelected && group.permissions.some((p) => selectedPermissions.has(p));

    return (
      <Box key={group.key} sx={{ mb: "16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            mb: "6px",
            cursor: "pointer",
          }}
          onClick={() => toggleGroup(group)}
        >
          <Box
            component="input"
            type="checkbox"
            checked={allSelected}
            ref={(el: HTMLInputElement | null) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={() => toggleGroup(group)}
            sx={{
              accentColor: "var(--accent)",
              width: 14,
              height: 14,
              cursor: "pointer",
            }}
          />
          <Box
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "var(--text-primary)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {group.label}
          </Box>
        </Box>
        {group.permissions.map((perm) => (
          <Box
            key={perm}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              pl: "20px",
              py: "2px",
              cursor: "pointer",
            }}
            onClick={() => togglePermission(perm)}
          >
            <Box
              component="input"
              type="checkbox"
              checked={selectedPermissions.has(perm)}
              onChange={() => togglePermission(perm)}
              sx={{
                accentColor: "var(--accent)",
                width: 13,
                height: 13,
                cursor: "pointer",
              }}
            />
            <Box sx={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
              {permissionLabel(perm)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {groups.map(renderGroup)}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sites Checkboxes Component
// ---------------------------------------------------------------------------

function SitesSelector({
  sites,
  selectedSiteIds,
  onChange,
}: Readonly<{
  sites: Site[];
  selectedSiteIds: Set<string>;
  onChange: (ids: Set<string>) => void;
}>) {
  const { t } = useTranslation(["roles"]);
  const toggleSite = (siteId: string) => {
    const next = new Set(selectedSiteIds);
    if (next.has(siteId)) {
      next.delete(siteId);
    } else {
      next.add(siteId);
    }
    onChange(next);
  };

  if (sites.length === 0) {
    return (
      <Box sx={{ color: "var(--text-tertiary)", fontSize: "0.8125rem" }}>
        {t("roles:noSitesAvailable")}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: 160,
        overflowY: "auto",
        border: "1px solid var(--border-mid)",
        borderRadius: "8px",
        p: "8px",
        backgroundColor: "var(--bg-raised)",
      }}
    >
      {sites.map((site) => (
        <Box
          key={site.siteId}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            py: "3px",
            cursor: "pointer",
          }}
          onClick={() => toggleSite(site.siteId)}
        >
          <Box
            component="input"
            type="checkbox"
            checked={selectedSiteIds.has(site.siteId)}
            onChange={() => toggleSite(site.siteId)}
            sx={{
              accentColor: "var(--accent)",
              width: 14,
              height: 14,
              cursor: "pointer",
            }}
          />
          <Box sx={{ fontSize: "0.8125rem", color: "var(--text-primary)" }}>
            {site.name}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2Roles() { // NOSONAR
  const { t } = useTranslation(["roles", "common", "errors"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { user: authUser } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // -- Data -----------------------------------------------------------------
  const { data: roles, isLoading: rolesLoading } = useGetRoles(customerId);
  const { data: sitesData } = useSites(customerId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sites = sitesData?.sites ?? [];

  const sitesMap = useMemo(() => {
    const m = new Map<string, Site>();
    for (const s of sites) {
      m.set(s.siteId, s);
    }
    return m;
  }, [sites]);

  // -- Mutations ------------------------------------------------------------
  const createRoleMutation = useCreateRole(customerId ?? "");
  const editRoleMutation = useEditRole(customerId ?? "");
  const deleteRoleMutation = useDeleteRole(customerId ?? "");

  // -- Selection ------------------------------------------------------------
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const selectedRole =
    (roles as Role[] | undefined)?.find((r) => r.roleId === selectedRoleId) ?? null;

  // -- Flyout state ---------------------------------------------------------
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutMode, setFlyoutMode] = useState<"create" | "edit">("create");

  // -- Flyout form state ----------------------------------------------------
  const [formName, setFormName] = useState("");
  const [formAllSites, setFormAllSites] = useState(true);
  const [formSelectedSiteIds, setFormSelectedSiteIds] = useState<Set<string>>(new Set());
  const [formSelectedPermissions, setFormSelectedPermissions] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState("");

  // -- Delete confirmation state --------------------------------------------
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // -- Permissions ----------------------------------------------------------
  const canCreate = authUser?.hasPermission("roles:Create") ?? false;
  const canEdit = authUser?.hasPermission("roles:Update") ?? false;
  const canDelete = authUser?.hasPermission("roles:Delete") ?? false;

  // -- Mutation success handler ---------------------------------------------
  const handleMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["roles", customerId] });
  };

  // -- Reset form -----------------------------------------------------------
  const resetForm = () => {
    setFormName("");
    setFormAllSites(true);
    setFormSelectedSiteIds(new Set());
    setFormSelectedPermissions(new Set());
    setFormError("");
    setShowDeleteConfirm(false);
    setDeleteError("");
  };

  // -- Populate form from role ----------------------------------------------
  const populateFromRole = (role: Role) => {
    setFormName(role.name);
    setFormAllSites(role.allSites);
    setFormSelectedSiteIds(new Set(role.siteIds ?? []));
    setFormSelectedPermissions(new Set(role.permissions));
    setFormError("");
    setShowDeleteConfirm(false);
    setDeleteError("");
  };

  // -- Handlers -------------------------------------------------------------
  const handleRowSelect = (roleId: string) => {
    if (!canEdit) {
      setSelectedRoleId((prev) => (prev === roleId ? null : roleId));
      return;
    }
    const role = (roles as Role[] | undefined)?.find((r) => r.roleId === roleId);
    if (!role) return;

    if (selectedRoleId === roleId && flyoutOpen) {
      setFlyoutOpen(false);
      setSelectedRoleId(null);
      return;
    }

    setSelectedRoleId(roleId);
    setFlyoutMode("edit");
    setFlyoutOpen(true);
    populateFromRole(role);
  };

  useEffect(() => {
    if (flyoutOpen && flyoutMode === "edit" && selectedRole) {
      populateFromRole(selectedRole);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole?.roleId]);

  const handleCreate = () => {
    setSelectedRoleId(null);
    setFlyoutMode("create");
    setFlyoutOpen(true);
    resetForm();
  };

  const handleCloseFlyout = () => {
    setFlyoutOpen(false);
    resetForm();
  };

  const handleSave = () => {
    if (!formName.trim()) {
      setFormError(t("roles:roleNameRequired"));
      return;
    }
    setFormError("");

    if (flyoutMode === "create") {
      createRoleMutation.mutate(
        {
          name: formName.trim(),
          allSites: formAllSites,
          siteIds: formAllSites ? [] : Array.from(formSelectedSiteIds),
          permissions: Array.from(formSelectedPermissions),
        },
        {
          onSuccess: () => {
            handleMutationSuccess();
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("roles:failedToCreateRole")),
        },
      );
    } else if (selectedRole) {
      editRoleMutation.mutate(
        {
          roleId: selectedRole.roleId,
          name: formName.trim(),
          allSites: formAllSites,
          siteIds: formAllSites ? [] : Array.from(formSelectedSiteIds),
          permissions: Array.from(formSelectedPermissions),
        },
        {
          onSuccess: () => {
            handleMutationSuccess();
            setFlyoutOpen(false);
            resetForm();
          },
          onError: (err) => setFormError(err.message || t("roles:failedToUpdateRole")),
        },
      );
    }
  };

  const handleDelete = () => {
    if (!selectedRole) return;
    setDeleteError("");
    deleteRoleMutation.mutate(
      { roleId: selectedRole.roleId },
      {
        onSuccess: () => {
          setSelectedRoleId(null);
          handleMutationSuccess();
          setFlyoutOpen(false);
          resetForm();
        },
        onError: (err) => {
          if (err instanceof ResourceInUseError) {
            setDeleteError(err.message);
          } else {
            setDeleteError(err.message || "Failed to delete role.");
          }
        },
      },
    );
  };

  // -- Site names helper ----------------------------------------------------
  const getSiteNames = (role: Role): string => {
    if (role.allSites) return t("roles:sites"); // "All Sites" - reuse sites label
    const siteIds = role.siteIds ?? [];
    if (siteIds.length === 0) return "\u2014";
    return siteIds
      .map((id) => sitesMap.get(id)?.name ?? id)
      .join(", ");
  };

  // -- Permissions display helper -------------------------------------------
  const getPermissionsDisplay = (role: Role): string => {
    const count = role.permissions.length;
    if (count === 0) return "\u2014";
    if (count > 3) return `${count} ${t("common:permissions").toLowerCase()}`;
    return role.permissions.map(permissionLabel).join(", ");
  };

  const isSaving = createRoleMutation.isPending || editRoleMutation.isPending;

  // -- Render ---------------------------------------------------------------

  const toolbarContent = canCreate ? (
    <Box component="button" onClick={handleCreate} sx={toolbarBtnSx(false)}>
      <PlusIcon />
      {t("roles:createRole")}
    </Box>
  ) : null;

  const tableContent = rolesLoading ? (
    <LoadingState message={t("roles:loadingRoles")} />
  ) : !roles || (roles as Role[]).length === 0 ? (
    <EmptyState message={t("roles:noRolesFound")} />
  ) : (
    <Box component="table" sx={crudTableSx}>
      <thead>
        <tr>
          <Box component="th" sx={{ width: 40, textAlign: "center" }}>
            {/* Radio header -- intentionally blank */}
          </Box>
          <th>{t("roles:name")}</th>
          <th>{t("common:sites")}</th>
          <th>{t("common:permissions")}</th>
        </tr>
      </thead>
      <tbody>
        {(roles as Role[]).map((role) => {
          const isSelected = selectedRoleId === role.roleId;
          return (
            <Box
              component="tr"
              key={role.roleId}
              onClick={() => handleRowSelect(role.roleId)}
              sx={selectedRowSx(isSelected)}
            >
              {/* Radio */}
              <Box component="td" sx={{ textAlign: "center" }}>
                <Box
                  component="input"
                  type="radio"
                  name="role-select"
                  checked={isSelected}
                  onChange={() => handleRowSelect(role.roleId)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={radioInputSx}
                />
              </Box>

              {/* Name */}
              <Box component="td" sx={primaryCellSx}>
                {role.name}
              </Box>

              {/* Sites */}
              <td>{getSiteNames(role)}</td>

              {/* Permissions */}
              <td>{getPermissionsDisplay(role)}</td>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );

  const { flyoutTitle, saveLabel } = flyoutLabels(flyoutMode, isSaving, {
    createTitle: t("roles:createRole"), editTitle: t("roles:editRole"),
    creating: t("roles:creatingRole"), saving: t("roles:savingRole"), saveChanges: t("common:saveChanges"),
  });

  return (
    <CrudPageLayout
      toolbar={toolbarContent}
      extra={
        <FlyoutDrawer
          open={flyoutOpen}
          onClose={handleCloseFlyout}
          title={flyoutTitle}
          footer={
            <FlyoutFooter
              onCancel={handleCloseFlyout}
              cancelLabel={t("common:cancel")}
              onSave={handleSave}
              saveLabel={saveLabel}
              isSaving={isSaving}
              showDelete={flyoutMode === "edit" && canDelete && !showDeleteConfirm}
              onDelete={() => setShowDeleteConfirm(true)}
              deleteLabel={t("roles:deleteRole")}
            />
          }
        >
          {formError && (
            <Box sx={formErrorSx}>{formError}</Box>
          )}

          {/* Name */}
          <Box>
            <Box sx={flyoutLabelSx}>{t("roles:name")}</Box>
            <Box
              component="input"
              type="text"
              placeholder={t("roles:roleName")}
              value={formName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
              sx={flyoutInputSx}
            />
          </Box>

          {/* All Sites Checkbox */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
            onClick={() => setFormAllSites(!formAllSites)}
          >
            <Box
              component="input"
              type="checkbox"
              checked={formAllSites}
              onChange={() => setFormAllSites(!formAllSites)}
              sx={{
                accentColor: "var(--accent)",
                width: 14,
                height: 14,
                cursor: "pointer",
              }}
            />
            <Box sx={{ fontSize: "0.875rem", color: "var(--text-primary)", fontWeight: 500 }}>
              {t("roles:grantAccessToAllSites")}
            </Box>
          </Box>

          {/* Sites selector (only when allSites is unchecked) */}
          {!formAllSites && (
            <Box>
              <Box sx={flyoutLabelSx}>{t("common:sites")}</Box>
              <SitesSelector
                sites={sites}
                selectedSiteIds={formSelectedSiteIds}
                onChange={setFormSelectedSiteIds}
              />
            </Box>
          )}

          {/* Permissions */}
          <Box>
            <Box sx={flyoutLabelSx}>{t("common:permissions")}</Box>
            <Box
              sx={{
                border: "1px solid var(--border-mid)",
                borderRadius: "8px",
                p: "12px",
                backgroundColor: "var(--bg-raised)",
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <PermissionsEditor
                selectedPermissions={formSelectedPermissions}
                onChange={setFormSelectedPermissions}
              />
            </Box>
          </Box>

          {/* Delete confirmation */}
          {flyoutMode === "edit" && showDeleteConfirm && selectedRole && (
            <DeleteConfirmation
              message={t("roles:deleteRoleConfirm", { name: selectedRole.name })}
              error={deleteError}
              onCancel={() => {
                setShowDeleteConfirm(false);
                setDeleteError("");
                deleteRoleMutation.reset();
              }}
              cancelLabel={t("common:cancel")}
              onConfirm={handleDelete}
              confirmLabel={t("common:confirmDeleteTitle")}
              isPending={deleteRoleMutation.isPending}
              pendingLabel={t("common:deleting")}
            />
          )}
        </FlyoutDrawer>
      }
    >
      {tableContent}
    </CrudPageLayout>
  );
}
